/**
 * A restaurant header for the menu screen.
 *
 * Leads with the one thing a customer standing outside needs: can I order right
 * now, and if not, when can I?
 */

import { Image, View } from 'react-native';
import {
  describeDayWith,
  formatDayTime,
  hoursOn,
  localDateKey,
  mapsUrl,
  telUrl,
  type RestaurantPublic,
} from '@yummy/shared';
import { Callout, Row, Stack, Text, ToneBadge, useT } from '@yummy/ui-rnr';
import { MapPin, Phone } from 'lucide-react-native';

import { ContactRow } from './ContactRow';
import { VerifiedMark } from './VerifiedMark';

/**
 * Shows restaurant identity and current ordering availability.
 *
 * The running total is deliberately not repeated here: the footer bar is on
 * screen at the same time and already carries the items and their total, so a
 * second copy of it in the header was the same number twice.
 *
 * @param props The restaurant profile.
 * @returns The menu header.
 */
export function RestaurantHeader({ restaurant }: { restaurant: RestaurantPublic }) {
  const t = useT();

  // The restaurant's own day, not the browser's, and every window it keeps: a
  // lunch and a dinner are two rows, and the header lists both.
  const today = hoursOn(restaurant, localDateKey(new Date(), restaurant.timezone));

  // A day is open when it has at least one window, and a closure marker closes
  // it whatever else is stored for the weekday.
  const openToday = today.some((row) => !row.is_closed);

  // The answer to the bar's question, and it sits directly under the hours that
  // raise it rather than at the end of the header. It is the reason the customer
  // opened this screen; the address and the number are not.
  const callout = !restaurant.accepting_orders ? (
    <Callout
      tone="warning"
      title={t('customer.menu.notTakingOrders')}
      message={t('customer.menu.notTakingOrdersHint')}
      size="base"
    />
  ) : !restaurant.is_open_now ? (
    <Callout
      tone="info"
      title={t('customer.menu.closedNow')}
      message={
        restaurant.next_opening_at
          ? t('customer.menu.closedScheduleHint', {
              when: formatDayTime(restaurant.next_opening_at, restaurant.timezone, undefined, t),
            })
          : t('customer.menu.closedCheckBack')
      }
      size="base"
    />
  ) : null;

  return (
    <Stack gap="md">
      {restaurant.cover_url ? (
        <Image
          source={{ uri: restaurant.cover_url }}
          // The ratio the merchant's own uploader crops a cover to, so what they
          // framed is what the customer sees, and square at the corners: it is a
          // banner across the top of the menu rather than a card in it.
          className="bg-muted aspect-[5/2] w-full"
          resizeMode="cover"
          accessibilityIgnoresInvertColors
        />
      ) : null}

      <Stack gap="xs">
        {/* The name and the mark share a row: the mark belongs to the name rather
            than to the hours below it. `align="start"` with a small offset on the
            mark so the bowl sits on the name's first line whether the name runs
            to one line or two — the row's own centring would drop it between
            them. */}
        <Row gap="sm" align="start">
          <Text
            // Watched by the menu bar, which takes the name over once this has
            // scrolled behind it.
            nativeID="menu-restaurant-name"
            numberOfLines={2}
            className="text-2xl font-semibold">
            {restaurant.name}
          </Text>
          <View className="mt-1">
            <VerifiedMark since={restaurant.verified_since} timeZone={restaurant.timezone} />
          </View>
        </Row>
        {/* No `wrap` on the row: a day holds several windows now, and its hours
            are long enough to want a second line. Wrapping the row moved the
            whole line below the badge and left the badge alone on an empty one;
            the hours take the width left over and wrap inside it instead.
            The badge is a pill with padding of its own, and `ToneBadge` anchors
            it to the top of the row, so the row's alignment cannot move it and
            its label lands two points below the hours' line — four points of
            padding around a sixteen-point label puts its centre at twelve where
            the hours' twenty-point line has its centre at ten. `-mt-0.5` lifts
            the pill by that difference, so the label inside it sits on the
            hours' line whether they run to one line or three, and the pill keeps
            the shape it has everywhere else. */}
        <Row gap="sm" align="start">
          {/* The chip is the positive case only. `can_order_now` is false in
              exactly the states the callout below explains in full — closed, or
              not taking orders — so a `Closed` chip there put the same word
              twice, once above the sentence saying it. */}
          {restaurant.can_order_now ? (
            <ToneBadge
              label={t('common.openNow')}
              tone="success"
              emphasis="solid"
              size="md"
              className="-mt-0.5"
            />
          ) : null}
          {/* The hours only when there are hours to give. A day with no window
              has nothing to say beyond the badge beside it, and the line read
              `Pickup only · Closed` directly after a chip saying `Closed`. */}
          {openToday ? (
            <Text className="text-subtle-foreground min-w-0 flex-1 text-[15px]/5">
              {t('customer.menu.pickupOnlyHours', {
                hours: describeDayWith(today, t('common.closed'), t('common.overnightSuffix')),
              })}
            </Text>
          ) : null}
        </Row>
      </Stack>

      {callout}

      {/* Both rows always, on a menu opened from a table sticker and on one
          opened from a shared link: the customer at the table is collecting from
          the counter either way, and the one who followed a link is deciding
          whether to walk over at all. */}
      <Stack gap="xs">
        {restaurant.address ? (
          <ContactRow
            icon={MapPin}
            label={t('common.openInMaps', { address: restaurant.address })}
            value={restaurant.address}
            url={mapsUrl(restaurant.address)}
          />
        ) : null}
        {restaurant.phone ? (
          <ContactRow
            icon={Phone}
            label={t('common.callRestaurant', { phone: restaurant.phone })}
            value={restaurant.phone}
            url={telUrl(restaurant.phone)}
          />
        ) : null}
      </Stack>

      {restaurant.description ? (
        <Text className="text-muted-foreground text-sm">{restaurant.description}</Text>
      ) : null}
    </Stack>
  );
}
